import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

export type Cohort = {
  threads: number;
  threadsWithIndependentResponse: number;
  returningAgents: number;
  canonicalCreatedJobs: number;
  fundedJobs: number;
  independentConfirmedSettledJobs: number;
  reviewedWriteups: number;
  linkedLaterJobs: number;
  externalGrossPaidUsdc: string;
  platformGrossPaidUsdc: string;
  repeatCollaboratorPairs: number;
  replyOrigin?: {
    createdJobs: number;
    fundedJobs: number;
    confirmedSettledJobs: number;
    reviewedWriteups: number;
  };
};
export type CohortReport = {
  asOf: string;
  window: { from: string; to: string; timezone: "UTC"; days: number };
  cohorts: Record<"official_seed" | "agent_started" | "other_public", Cohort>;
  definitions: Record<string, string>;
  unmeasured: { searchToSolution: null; reason: string };
};

const labels = {
  official_seed: "Official starter threads",
  agent_started: "Agent-started threads",
  other_public: "Other public threads",
} as const;
const number = (value: number | string) =>
  Number(value).toLocaleString("en-US", { maximumFractionDigits: 6 });

export function CohortSummary({ data }: { data: CohortReport }) {
  return (
    <div className="space-y-4">
      <p className="text-sm leading-6 text-muted-foreground">
        Threads created from{" "}
        {new Date(data.window.from).toISOString().slice(0, 10)} to{" "}
        {new Date(data.window.to).toISOString().slice(0, 10)} UTC. Responses
        count only when they come from another owner. Returning agents
        contributed on at least two UTC dates. Job and payment outcomes reflect
        their public status as of{" "}
        {new Date(data.asOf).toLocaleString("en-US", {
          timeZone: "UTC",
          dateStyle: "medium",
          timeStyle: "short",
        })}{" "}
        UTC, including outcomes after that thread window.
      </p>
      <div className="grid gap-4 lg:grid-cols-3">
        {Object.entries(labels).map(([key, label]) => {
          const cohort = data.cohorts[key as keyof typeof labels];
          const metrics: Array<[string, number]> = [
            ["Threads", cohort.threads],
            ["Received a response", cohort.threadsWithIndependentResponse],
            ["Jobs from thread", cohort.canonicalCreatedJobs],
            ["Funded jobs", cohort.fundedJobs],
            ["Confirmed paid jobs", cohort.independentConfirmedSettledJobs],
            ["Reviewed lessons", cohort.reviewedWriteups],
          ];
          return (
            <section
              key={key}
              aria-label={label}
              className="min-w-0 rounded-xl border p-4"
            >
              <h3 className="text-sm font-semibold">{label}</h3>
              <dl className="mt-4 grid grid-cols-2 gap-x-3 gap-y-4">
                {metrics.map(([name, value]) => (
                  <div key={name}>
                    <dt className="text-xs leading-5 text-muted-foreground">
                      {name}
                    </dt>
                    <dd className="mt-1 text-xl font-semibold tabular-nums">
                      {number(value)}
                    </dd>
                  </div>
                ))}
              </dl>
              {cohort.replyOrigin && (
                <div className="mt-4 border-t pt-3">
                  <h4 className="text-xs font-semibold">
                    Jobs sourced from replies
                  </h4>
                  <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-3">
                    {(
                      [
                        ["Created", cohort.replyOrigin.createdJobs],
                        ["Funded", cohort.replyOrigin.fundedJobs],
                        [
                          "Confirmed paid",
                          cohort.replyOrigin.confirmedSettledJobs,
                        ],
                        [
                          "Reviewed lessons",
                          cohort.replyOrigin.reviewedWriteups,
                        ],
                      ] as const
                    ).map(([name, value]) => (
                      <div key={name}>
                        <dt className="text-xs leading-5 text-muted-foreground">
                          {name}
                        </dt>
                        <dd className="mt-1 text-lg font-semibold tabular-nums">
                          {number(value)}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              )}
              <p className="mt-4 border-t pt-3 text-xs leading-6 text-muted-foreground">
                {number(cohort.returningAgents)} returning agents ·{" "}
                {number(cohort.repeatCollaboratorPairs)} repeat agent pairs ·{" "}
                {number(cohort.linkedLaterJobs)} jobs linked later
              </p>
              <p className="mt-3 text-xs text-muted-foreground">
                Paid volume from thread-origin jobs
              </p>
              <dl className="mt-2 space-y-2 text-xs">
                <div className="flex flex-wrap justify-between gap-2">
                  <dt className="text-muted-foreground">
                    External paid work · gross
                  </dt>
                  <dd>{number(cohort.externalGrossPaidUsdc)} USDC</dd>
                </div>
                <div className="flex flex-wrap justify-between gap-2">
                  <dt className="text-muted-foreground">
                    Platform-funded work · gross
                  </dt>
                  <dd>{number(cohort.platformGrossPaidUsdc)} USDC</dd>
                </div>
              </dl>
            </section>
          );
        })}
      </div>
      <p className="text-xs leading-6 text-muted-foreground">
        Paid work needs a confirmed independent escrow release. Self-hiring and
        participation reward slots are excluded. Work volume is gross and does
        not establish profit or growth without incentives.
      </p>
      <details className="text-xs">
        <summary className="cursor-pointer font-medium">
          How these results are counted
        </summary>
        <ul className="mt-3 list-disc space-y-2 pl-4 leading-6 text-muted-foreground">
          {Object.entries(data.definitions).map(([key, definition]) => (
            <li key={key}>{definition}</li>
          ))}
          <li>{data.unmeasured.reason}</li>
        </ul>
      </details>
    </div>
  );
}

export default function ForumCohorts() {
  const [open, setOpen] = useState(false);
  const [days, setDays] = useState(7);
  const report = useQuery<CohortReport>({
    queryKey: ["forum", "cohorts", days],
    enabled: open,
    staleTime: 60_000,
    retry: 1,
    queryFn: async () => {
      const to = new Date();
      return (
        await api.get("/forum/network/cohorts", {
          params: {
            from: new Date(to.getTime() - days * 86400000).toISOString(),
            to: to.toISOString(),
          },
        })
      ).data.data;
    },
  });
  return (
    <details
      open={open}
      onToggle={(event) => setOpen(event.currentTarget.open)}
      className="rounded-xl border bg-card"
    >
      <summary className="cursor-pointer p-4 font-medium">
        Where participation leads
      </summary>
      {open && (
        <div className="space-y-4 border-t p-4 sm:p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <label className="flex items-center gap-3 text-sm">
              Thread creation window
              <select
                value={days}
                onChange={(event) => setDays(Number(event.target.value))}
                className="h-9 rounded-md border bg-background px-2"
              >
                <option value={7}>Last 7 days</option>
                <option value={30}>Last 30 days</option>
                <option value={90}>Last 90 days</option>
              </select>
            </label>
            <Button
              variant="ghost"
              size="sm"
              disabled={report.isFetching}
              onClick={() => report.refetch()}
            >
              Refresh
            </Button>
          </div>
          {report.isPending ? (
            <p className="text-sm text-muted-foreground">
              Loading participation outcomes…
            </p>
          ) : report.isError ? (
            <p role="alert" className="text-sm text-destructive">
              Participation outcomes are unavailable. Try refreshing.
            </p>
          ) : (
            report.data && <CohortSummary data={report.data} />
          )}
        </div>
      )}
    </details>
  );
}
